import type {
  NativeLabOrderSetFailureReasonT,
  SubmitNativeLabOrderSetRequestT,
} from '@lotushealth/lotushealth_js_sdk/schemas';

import type { SubmitNativeLabOrderSetResponseT } from '@/entities/labs';
import { extractApiErrorDetail, logger } from '@/shared';

export type NativeLabOrderSubmissionData = Omit<SubmitNativeLabOrderSetRequestT, 'orderAttemptId'>;

interface Attempt {
  userId: string;
  data: NativeLabOrderSubmissionData;
  orderAttemptId: string;
}

interface Success {
  remoteOrderId: string;
}

type Failure = {
  status: 'failure';
  reason: NativeLabOrderSetFailureReasonT;
  errorMessage: string;
  retryData?: NativeLabOrderSubmissionData;
};

type Outcome =
  | { status: 'idle' }
  | { status: 'pending'; attempt: Attempt }
  | { status: 'failure'; attempt: Attempt; failure: Failure }
  | { status: 'success'; attempt: Attempt; success: Success };

export type NativeLabOrderSubmissionState =
  | { status: 'idle' | 'submitting' | 'recovering' | 'success'; errorMessage: null }
  | Failure;

const RETRY_POLICY = {
  invalid_order: 'new_attempt',
  clinician_information_missing: 'new_attempt',
  patient_information_missing: 'new_attempt',
  insurance_information_missing: 'new_attempt',
  insurance_not_supported: 'new_attempt',
  lab_ordering_unavailable: 'same_attempt',
  order_in_progress: 'same_attempt',
  order_rejected: 'new_attempt',
  order_recording_failed: 'same_attempt',
  submission_status_unknown: 'same_attempt',
} as const satisfies Record<NativeLabOrderSetFailureReasonT, 'new_attempt' | 'same_attempt'>;

// Keep only attempts that need remount recovery. Safe terminal transitions
// unregister them; pending or uncertain attempts remain for this browser session
// because forgetting their idempotency token could allow a duplicate order.
const records = new Map<string, NativeLabOrderAttemptRecord>();

function unregister(record: NativeLabOrderAttemptRecord): void {
  if (record.recoveryKey && records.get(record.recoveryKey) === record) {
    records.delete(record.recoveryKey);
  }
}

export class NativeLabOrderAttemptRecord {
  private outcome: Outcome = { status: 'idle' };
  private inFlight: Promise<Success | null> | null = null;
  private readonly listeners = new Set<() => void>();
  readonly userId: string;
  readonly recoveryKey?: string;

  constructor(userId: string, recoveryKey?: string) {
    this.userId = userId;
    this.recoveryKey = recoveryKey;
  }

  readonly getSnapshot = (): Outcome => this.outcome;

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  get isPending(): boolean {
    return this.outcome.status === 'pending';
  }

  get isEditableFailure(): boolean {
    return this.outcome.status === 'failure' && this.outcome.failure.retryData === undefined;
  }

  get retryData(): NativeLabOrderSubmissionData | undefined {
    switch (this.outcome.status) {
      case 'pending':
      case 'success':
        return this.outcome.attempt.data;
      case 'failure':
        return this.outcome.failure.retryData;
      case 'idle':
        return undefined;
    }
  }

  toState(isJoinedByCurrentHook: boolean): NativeLabOrderSubmissionState {
    switch (this.outcome.status) {
      case 'idle':
        return { status: 'idle', errorMessage: null };
      case 'pending':
        return {
          status: isJoinedByCurrentHook ? 'submitting' : 'recovering',
          errorMessage: null,
        };
      case 'failure':
        return this.outcome.failure;
      case 'success':
        return { status: 'success', errorMessage: null };
    }
  }

  submit(
    data: NativeLabOrderSubmissionData,
    execute: (attempt: Attempt) => Promise<SubmitNativeLabOrderSetResponseT>
  ): Promise<Success | null> {
    if (this.outcome.status === 'success') return Promise.resolve(this.outcome.success);
    if (this.inFlight) return this.inFlight;

    const attempt =
      this.outcome.status === 'idle'
        ? { userId: this.userId, data, orderAttemptId: crypto.randomUUID() }
        : this.outcome.attempt;
    if (this.recoveryKey) records.set(this.recoveryKey, this);
    this.publish({ status: 'pending', attempt });

    const inFlight = Promise.resolve()
      .then(() => execute(attempt))
      .then((result): Success | null => {
        if (result.type === 'success') {
          const success = { remoteOrderId: result.order.remote_id };
          this.publish({ status: 'success', attempt, success });
          return success;
        }

        const retryData = RETRY_POLICY[result.reason] === 'same_attempt' ? attempt.data : undefined;
        const failure = {
          status: 'failure',
          reason: result.reason,
          errorMessage: result.details,
          retryData,
        } satisfies Failure;
        this.publish({ status: 'failure', attempt, failure });
        if (!retryData) unregister(this);
        return null;
      })
      .catch((error: unknown): null => {
        logger.error(
          'native lab order submission failed',
          {
            surface: 'handled',
            feature: 'lab-order-form',
            operation: 'submit-native-order-set',
            attemptId: attempt.orderAttemptId,
            apiErrorDetail: extractApiErrorDetail(error),
          },
          error
        );
        const failure = {
          status: 'failure',
          reason: 'submission_status_unknown',
          errorMessage:
            'Lotus could not confirm whether the order was accepted. Retry this exact order.',
          retryData: attempt.data,
        } satisfies Failure;
        this.publish({ status: 'failure', attempt, failure });
        return null;
      })
      .finally(() => {
        if (this.inFlight === inFlight) this.inFlight = null;
      });

    this.inFlight = inFlight;
    return inFlight;
  }

  private publish(outcome: Outcome): void {
    this.outcome = outcome;
    for (const listener of this.listeners) listener();
  }
}

export const nativeLabOrderAttemptRegistry = {
  acquire(userId: string, attemptKey?: string): NativeLabOrderAttemptRecord {
    if (!attemptKey) return new NativeLabOrderAttemptRecord(userId);
    const recoveryKey = JSON.stringify([userId, attemptKey]);
    return records.get(recoveryKey) ?? new NativeLabOrderAttemptRecord(userId, recoveryKey);
  },

  replace(record: NativeLabOrderAttemptRecord): NativeLabOrderAttemptRecord {
    unregister(record);
    return new NativeLabOrderAttemptRecord(record.userId, record.recoveryKey);
  },

  release(record: NativeLabOrderAttemptRecord): void {
    unregister(record);
  },
};
